import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface AssetRow {
  readonly assetId: string;
  readonly assetCode: string;
  readonly assetName: string;
  readonly assetType: string;
  readonly classification: string;
  readonly criticality: string;
  readonly environment: string;
  readonly encryptionStatus: string;
  readonly status: string;
}

const TYPES = ['', 'APPLICATION', 'DATABASE', 'SERVER', 'API', 'DOCUMENT', 'USER', 'VENDOR', 'INTEGRATION', 'NETWORK', 'ENDPOINT', 'DATA_STORE'];
const CRITICALITIES = ['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const CREATE_TYPES = TYPES.filter(Boolean);
const CREATE_CRITICALITIES = CRITICALITIES.filter(Boolean);

/**
 * M30 — Security Asset Register (§23 / §31). Central inventory of security-
 * relevant assets (applications, databases, servers, APIs, data stores …) with
 * their classification, criticality, environment and encryption posture. Enforces
 * BR-06 — sensitive (PII / CONFIDENTIAL+) assets must be encrypted at registration.
 * Registering, updating and decommissioning are wired to the guarded
 * /v1/security/assets write API.
 */
export function M30SecurityAssetRegisterPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<AssetRow[]>([]);
  const [assetType, setAssetType] = useState('');
  const [criticality, setCriticality] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('APPLICATION');
  const [newCriticality, setNewCriticality] = useState('MEDIUM');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (assetType) params.set('assetType', assetType);
    if (criticality) params.set('criticality', criticality);
    const qs = params.toString();
    apiRequest<{ data: AssetRow[] }>(`/v1/security/assets${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, assetType, criticality, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m30.asset.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(tr('m30.asset.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: AssetRow }>(`/v1/security/assets`, {
        method: 'POST',
        body: { assetName: name.trim(), assetType: newType, criticality: newCriticality },
        token,
      });
      setNotice(tr('m30.asset.registered', { code: r.data.assetCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m30.asset.verbRegister'), 'M30_ASSET_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const update = (a: AssetRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/assets/${a.assetId}`, { method: 'PUT', body: { criticality: newCriticality }, token }),
      tr('m30.asset.updated', { code: a.assetCode }), tr('m30.asset.verbUpdate'), 'M30_ASSET_MANAGE',
    );

  const decommission = (a: AssetRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/assets/${a.assetId}/decommission`, { method: 'POST', body: {}, token }),
      tr('m30.asset.decommissioned', { code: a.assetCode }), tr('m30.asset.verbDecommission'), 'M30_ASSET_MANAGE',
    );

  return (
    <main aria-label="asset-register">
      <h1>{tr('m30.asset.title')}</h1>

      <section aria-label="create-asset">
        <h2>{tr('m30.asset.registerHeading')}</h2>
        <form aria-label="asset-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m30.asset.fieldName')}<input aria-label="field-asset-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m30.asset.fieldType')}
            <select aria-label="field-asset-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m30.asset.fieldCriticality')}
            <select aria-label="field-criticality" value={newCriticality} onChange={(e) => setNewCriticality(e.target.value)}>
              {CREATE_CRITICALITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-asset" disabled={busy}>{busy ? tr('m30.asset.submitRegistering') : tr('m30.asset.submitRegister')}</button>
        </form>
        {actionError && <p role="alert" aria-label="asset-error">{actionError}</p>}
        {notice && <p aria-label="asset-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m30.asset.filterType')}
          <select aria-label="filter-asset-type" value={assetType} onChange={(e) => setAssetType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m30.asset.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m30.asset.filterCriticality')}
          <select aria-label="filter-criticality" value={criticality} onChange={(e) => setCriticality(e.target.value)}>
            {CRITICALITIES.map((c) => <option key={c || 'all'} value={c}>{c || tr('m30.asset.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m30.asset.loading')}</p> : (
        <>
          <p aria-label="asset-count">{tr('m30.asset.count', { count: rows.length })}</p>
          <table aria-label="asset-table">
            <thead><tr><th>{tr('m30.asset.thCode')}</th><th>{tr('m30.asset.thName')}</th><th>{tr('m30.asset.thType')}</th><th>{tr('m30.asset.thClassification')}</th><th>{tr('m30.asset.thCriticality')}</th><th>{tr('m30.asset.thEnvironment')}</th><th>{tr('m30.asset.thEncryption')}</th><th>{tr('m30.asset.thStatus')}</th><th>{tr('m30.asset.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.assetId}>
                  <td>{a.assetCode}</td><td>{a.assetName}</td><td>{a.assetType}</td>
                  <td>{a.classification}</td><td>{a.criticality}</td><td>{a.environment}</td>
                  <td>{a.encryptionStatus}</td><td>{a.status}</td>
                  <td>
                    <button type="button" aria-label={`update-${a.assetCode}`} onClick={() => void update(a)}>{tr('m30.asset.btnUpdate')}</button>
                    <button type="button" aria-label={`decommission-${a.assetCode}`} onClick={() => void decommission(a)}>{tr('m30.asset.btnDecommission')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
